/* =====================================================
   SEGRETO LAB – SCROLL & ACCORDION ANIMATIONS
   Smooth, GPU-friendly, mobile-optimized
   - rAF-based scroll throttling
   - Single shared scroll listener for all zoom-in elements
   - Stable DOM-order cascade
   - will-change added before animation, removed after
   ===================================================== */

const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';
const ACCORDION_ANIMATED_CLASS = 'accordion--animated';

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---------- rAF-based throttling (smoother than setTimeout) ---------- */
function rafThrottle(fn) {
  let scheduled = false;
  return function throttled(...args) {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      fn.apply(this, args);
    });
  };
}

/* ===========================================================
   1) SCROLL REVEAL (fade-in / slide-in)
   =========================================================== */

function computeCascadeOrder(el) {
  // Use position among siblings that also have data-cascade.
  // Stable regardless of IntersectionObserver batch timing.
  const parent = el.parentElement;
  if (!parent) return 0;
  const siblings = parent.querySelectorAll(':scope > [data-cascade]');
  for (let i = 0; i < siblings.length; i++) {
    if (siblings[i] === el) return i;
  }
  return 0;
}

function onRevealIntersection(entries, observer) {
  entries.forEach((entry) => {
    const el = entry.target;
    if (entry.isIntersecting) {
      if (el.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        // Hint the compositor right before the animation plays
        el.style.willChange = 'transform, opacity';

        if (el.hasAttribute('data-cascade')) {
          el.style.setProperty('--animation-order', computeCascadeOrder(el));
        }

        el.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);

        // Remove will-change after animation ends to avoid permanent layer promotion
        const clear = () => {
          el.style.willChange = '';
          el.removeEventListener('animationend', clear);
          el.removeEventListener('transitionend', clear);
        };
        el.addEventListener('animationend', clear, { once: true });
        el.addEventListener('transitionend', clear, { once: true });
        // Safety fallback in case the animation event never fires
        setTimeout(clear, 1200);
      }
      observer.unobserve(el);
    } else {
      el.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      el.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(root = document, design = false) {
  const els = Array.from(root.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (!els.length) return;

  if (design) {
    els.forEach((el) => el.classList.add('scroll-trigger--design-mode'));
    return;
  }

  const observer = new IntersectionObserver(onRevealIntersection, {
    // Start the animation slightly before the element is fully on-screen
    rootMargin: '0px 0px -50px 0px',
    threshold: 0
  });

  els.forEach((el) => observer.observe(el));
}

/* ===========================================================
   2) ZOOM-IN ON SCROLL (single shared listener)
   =========================================================== */

const zoomInState = {
  items: [], // { el, visible }
  listening: false
};

function percentageSeen(el) {
  // Read layout once per frame, via shared scheduler below
  const vh = window.innerHeight;
  const rect = el.getBoundingClientRect();
  const top = rect.top + window.scrollY;
  const h = el.offsetHeight;
  const scrollY = window.scrollY;

  if (top > scrollY + vh) return 0;
  if (top + h < scrollY) return 100;

  const dist = (scrollY + vh) - top;
  return Math.round(dist / ((vh + h) / 100));
}

function updateZoomItems() {
  const scaleAmt = 0.2 / 100;
  // Batch read + write: read all first, then write all, to reduce layout thrash
  const updates = [];
  for (let i = 0; i < zoomInState.items.length; i++) {
    const item = zoomInState.items[i];
    if (!item.visible) continue;
    updates.push([item.el, 1 + scaleAmt * percentageSeen(item.el)]);
  }
  for (let i = 0; i < updates.length; i++) {
    updates[i][0].style.setProperty('--zoom-in-ratio', updates[i][1]);
  }
}

const scheduledZoomUpdate = rafThrottle(updateZoomItems);

function initializeScrollZoomAnimationTrigger() {
  if (prefersReducedMotion.matches) return;
  const els = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));
  if (!els.length) return;

  const visObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const item = zoomInState.items.find((it) => it.el === entry.target);
      if (item) item.visible = entry.isIntersecting;
    });
  }, { threshold: 0.1 });

  els.forEach((el) => {
    const item = { el, visible: false };
    zoomInState.items.push(item);
    visObserver.observe(el);
    // Set an initial value so nothing flashes at scale 1
    el.style.setProperty('--zoom-in-ratio', 1);
  });

  if (!zoomInState.listening) {
    window.addEventListener('scroll', scheduledZoomUpdate, { passive: true });
    window.addEventListener('resize', scheduledZoomUpdate, { passive: true });
    zoomInState.listening = true;
  }

  // Prime once in case something is already in view on load
  scheduledZoomUpdate();
}

/* ===========================================================
   3) ACCORDION ANIMATIONS
   =========================================================== */

function setupAccordionAnimations() {
  const accordions = document.querySelectorAll(`.${ACCORDION_ANIMATED_CLASS}`);
  accordions.forEach((accordion) => {
    if (accordion.dataset.sgAccordionReady === '1') return;
    accordion.dataset.sgAccordionReady = '1';

    const details = accordion.querySelector('details');
    const summary = accordion.querySelector('summary');
    const content = accordion.querySelector('.accordion__content');
    if (!details || !summary || !content) return;

    // If it starts open, make sure content is fully visible
    if (details.open) {
      content.style.maxHeight = 'none';
      content.style.opacity = '1';
    }

    let isAnimating = false;

    const endHandler = (finalMaxHeight, closeAfter) => {
      return function onEnd(evt) {
        if (evt.propertyName !== 'max-height') return;
        content.removeEventListener('transitionend', onEnd);
        content.style.willChange = '';
        if (finalMaxHeight !== null) content.style.maxHeight = finalMaxHeight;
        if (closeAfter) details.removeAttribute('open');
        isAnimating = false;
      };
    };

    summary.addEventListener('click', (e) => {
      // Let links inside summary behave normally
      if (e.target.closest('a')) return;
      e.preventDefault();
      if (isAnimating) return;

      isAnimating = true;
      content.style.willChange = 'max-height, opacity';

      if (details.open) {
        // Closing
        // Freeze current height, then transition to 0
        content.style.maxHeight = `${content.scrollHeight}px`;
        // Force layout flush so the following change is animated
        void content.offsetHeight;

        requestAnimationFrame(() => {
          content.style.maxHeight = '0';
          content.style.opacity = '0';
        });

        content.addEventListener('transitionend', endHandler(null, true));
      } else {
        // Opening
        details.setAttribute('open', '');
        content.style.maxHeight = '0';
        content.style.opacity = '0';

        requestAnimationFrame(() => {
          content.style.maxHeight = `${content.scrollHeight}px`;
          content.style.opacity = '1';
        });

        // After opening transitions complete, release max-height so responsive resizes don't clip content
        content.addEventListener('transitionend', endHandler('none', false));
      }
    });
  });
}

/* ===========================================================
   INIT
   =========================================================== */

function initAllAnimations() {
  try {
    initializeScrollAnimationTrigger();
    initializeScrollZoomAnimationTrigger();
    setupAccordionAnimations();
  } catch (err) {
    console.error('Animation init error:', err);
  }
}

if (document.readyState !== 'loading') {
  initAllAnimations();
} else {
  document.addEventListener('DOMContentLoaded', initAllAnimations);
}

/* Shopify section editor hooks */
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (e) => {
    initializeScrollAnimationTrigger(e.target, true);
    setupAccordionAnimations();
  });
  document.addEventListener('shopify:section:reorder', () => {
    initializeScrollAnimationTrigger(document, true);
    setupAccordionAnimations();
  });
}

/* If user enables reduced motion mid-session, stop zoom updates */
prefersReducedMotion.addEventListener && prefersReducedMotion.addEventListener('change', (e) => {
  if (e.matches && zoomInState.listening) {
    window.removeEventListener('scroll', scheduledZoomUpdate);
    window.removeEventListener('resize', scheduledZoomUpdate);
    zoomInState.listening = false;
    zoomInState.items.forEach((it) => it.el.style.setProperty('--zoom-in-ratio', 1));
  }
});
